import { Link } from '@tanstack/react-router';
import type { TransmissionType, UploadDetected } from '@tabservice/tabfile-client';
import { Check } from 'lucide-react';
import { Card, SectionHeader } from '@/components/ui/card';
import { ErrorNote } from '@/components/ui/feedback';
import { Pill } from '@/components/ui/pill';
import { Table, TableWrap, TBody, TD, TH, THead, TR } from '@/components/ui/table';

const typeWord: Record<TransmissionType, string> = {
  original: 'an original e-file',
  correction: 'a correction',
  replacement: 'a replacement',
};

/** True when the file names a prior e-file Tab Service cannot find on this account. */
export function hasUnverifiedReferences(detected: UploadDetected | undefined): boolean {
  return (detected?.priorReferences ?? []).some((reference) => reference.verified !== true);
}

/** The e-file the file is being amended against, if the references agree on one. */
export function parentFilingIdOf(detected: UploadDetected | undefined): string | null {
  for (const reference of detected?.priorReferences ?? []) {
    if (reference.verified === true && reference.efileId) return reference.efileId;
  }
  return null;
}

/**
 * An IRIS correction or replacement carries the IRS references of the records
 * it amends. Each one is checked against this account's e-file history, because
 * the IRS refuses a correction that points at a return somebody else filed.
 */
export function PriorReferences({
  detected,
  transmissionType,
}: {
  detected: UploadDetected;
  transmissionType: TransmissionType;
}) {
  const references = detected.priorReferences ?? [];
  const parentEfileId = parentFilingIdOf(detected);
  const unverified = references.filter((reference) => reference.verified !== true).length;

  return (
    <Card data-tour="prior-references">
      <SectionHeader
        title="Prior e-file references"
        description={`This file declares itself ${typeWord[transmissionType]}. Every reference in it is checked against the e-files Tab Service has already made for this account.`}
        actions={
          <Pill tone={unverified > 0 ? 'danger' : 'success'}>
            {unverified > 0 ? `${unverified} not recognised` : 'All recognised'}
          </Pill>
        }
      />

      {detected.originalReceiptId ? (
        <p className="pb-4 text-base text-muted-foreground">
          The file replaces receipt {detected.originalReceiptId}.
        </p>
      ) : null}

      {references.length === 0 ? (
        <p className="text-base text-muted-foreground">
          No prior e-file references were found in this file. A correction must reference the records it corrects.
        </p>
      ) : (
        <TableWrap>
          <Table>
            <THead>
              <tr>
                <TH>Record</TH>
                <TH>Receipt id</TH>
                <TH>Recognised</TH>
                <TH>E-file</TH>
              </tr>
            </THead>
            <TBody>
              {references.map((reference, index) => (
                <TR key={`${reference.recordRef ?? reference.recordId ?? index}`}>
                  <TD className="font-medium">{reference.recordRef ?? reference.recordId ?? 'not in the file'}</TD>
                  <TD className="text-muted-foreground">{reference.receiptId ?? '-'}</TD>
                  <TD>
                    {reference.verified === true ? (
                      <Pill tone="success">
                        <Check className="mr-1 h-3 w-3" aria-hidden="true" />
                        Verified
                      </Pill>
                    ) : (
                      <Pill tone="danger">Unknown</Pill>
                    )}
                  </TD>
                  <TD>
                    {reference.efileId ? (
                      <Link
                        to="/e-files/$efileId"
                        params={{ efileId: reference.efileId }}
                        className="text-link hover:underline"
                      >
                        {reference.efileId}
                      </Link>
                    ) : (
                      <span className="text-muted-foreground">-</span>
                    )}
                  </TD>
                </TR>
              ))}
            </TBody>
          </Table>
        </TableWrap>
      )}

      <div className="pt-5">
        {unverified > 0 ? (
          <ErrorNote
            title="One or more references do not match an e-file on this account"
            detail={`Tab Service cannot file ${typeWord[transmissionType]} against a return it did not send. Check the receipt and record ids in the file, or file the records as an original submission instead.`}
          />
        ) : parentEfileId ? (
          <p className="text-base text-muted-foreground">
            Tab Service will file this as {typeWord[transmissionType]} of filing{' '}
            <Link to="/e-files/$efileId" params={{ efileId: parentEfileId }} className="text-link hover:underline">
              {parentEfileId}
            </Link>
            .
          </p>
        ) : (
          <p className="text-base text-muted-foreground">
            Tab Service will file this as {typeWord[transmissionType]} of the returns these references identify.
          </p>
        )}
      </div>
    </Card>
  );
}
